export type MeetingLifecyclePhase = 'idle' | 'joining' | 'connected' | 'ended';

export type MeetingFeaturePhase =
  | 'off'
  | 'starting'
  | 'active'
  | 'stopping'
  | 'recovering'
  | 'failed';

export type MediaPublicationRole = 'camera' | 'screen' | 'translation' | 'unknown';

export interface MeetingContextState {
  roomId: string;
  meetingId: string;
  epoch: number;
  phase: MeetingLifecyclePhase;
}

export interface CameraPublicationState {
  role: 'camera';
  logicalId: string;
  phase: MeetingFeaturePhase;
  streamId: string;
  generation: number;
  hasAudio: boolean;
  hasVideo: boolean;
  audioEnabled: boolean;
  videoEnabled: boolean;
}

export interface ScreenPublicationState {
  role: 'screen';
  logicalId: string;
  desired: boolean;
  available: boolean;
  phase: MeetingFeaturePhase;
  streamId: string;
  generation: number;
  operationId: number;
  error: string;
}

export interface TranslationPublicationState {
  role: 'translation';
  logicalId: string;
  desired: boolean;
  phase: MeetingFeaturePhase;
  published: boolean;
  streamId: string;
  sourceStreamId: string;
  generation: number;
  operationId: number;
  error: string;
}

export interface MeetingState {
  context: MeetingContextState;
  camera: CameraPublicationState;
  screen: ScreenPublicationState;
  translation: TranslationPublicationState;
}

export type MeetingStateEvent =
  | { type: 'CONTEXT_OBSERVED'; roomId: string; meetingId: string }
  | { type: 'SESSION_ACTIVE_OBSERVED'; active: boolean }
  | { type: 'CAMERA_OBSERVED'; streamId: string; hasAudio: boolean; hasVideo: boolean }
  | { type: 'CAMERA_AUDIO_OBSERVED'; enabled: boolean }
  | { type: 'CAMERA_VIDEO_OBSERVED'; enabled: boolean }
  | { type: 'SCREEN_AVAILABILITY_OBSERVED'; available: boolean }
  | { type: 'SCREEN_OBSERVED'; active: boolean; streamId: string }
  | { type: 'SCREEN_COMMAND_STARTED'; desired: boolean; operationId: number }
  | { type: 'SCREEN_COMMAND_SUCCEEDED'; desired: boolean; operationId: number; streamId: string }
  | { type: 'SCREEN_COMMAND_FAILED'; desired: boolean; operationId: number; error: string }
  | { type: 'TRANSLATION_OBSERVED'; active: boolean; streamId: string }
  | { type: 'TRANSLATION_COMMAND_STARTED'; desired: boolean; operationId: number; sourceStreamId: string }
  | { type: 'TRANSLATION_COMMAND_SUCCEEDED'; desired: boolean; operationId: number; streamId: string }
  | { type: 'TRANSLATION_COMMAND_FAILED'; desired: boolean; operationId: number; error: string };

const featureOff = (): MeetingFeaturePhase => 'off';

export function createInitialMeetingState(): MeetingState {
  return {
    context: {
      roomId: null,
      meetingId: null,
      epoch: 0,
      phase: 'idle'
    },
    camera: {
      role: 'camera',
      logicalId: null,
      phase: featureOff(),
      streamId: null,
      generation: 0,
      hasAudio: false,
      hasVideo: false,
      audioEnabled: true,
      videoEnabled: true
    },
    screen: {
      role: 'screen',
      logicalId: null,
      desired: false,
      available: false,
      phase: featureOff(),
      streamId: null,
      generation: 0,
      operationId: 0,
      error: null
    },
    translation: {
      role: 'translation',
      logicalId: null,
      desired: false,
      phase: featureOff(),
      published: false,
      streamId: null,
      sourceStreamId: null,
      generation: 0,
      operationId: 0,
      error: null
    }
  };
}

function withPublicationIdentity(state: MeetingState, meetingId: string): MeetingState {
  return {
    ...state,
    camera: { ...state.camera, logicalId: meetingId ? `${meetingId}:local:camera` : null },
    screen: { ...state.screen, logicalId: meetingId ? `${meetingId}:local:screen` : null },
    translation: { ...state.translation, logicalId: meetingId ? `${meetingId}:local:translation` : null }
  };
}

function nextGeneration(currentStreamId: string, nextStreamId: string, generation: number): number {
  return nextStreamId && currentStreamId !== nextStreamId ? generation + 1 : generation;
}

function errorMessage(error: string): string {
  return error || 'unknown-error';
}

/**
 * Pure reducer for the meeting UI state. OpenTok/WebSocket objects deliberately
 * stay outside this model; only stable, serializable facts enter the reducer.
 */
export function reduceMeetingState(state: MeetingState, event: MeetingStateEvent): MeetingState {
  switch (event.type) {
    case 'CONTEXT_OBSERVED': {
      const contextChanged = state.context.roomId !== event.roomId || state.context.meetingId !== event.meetingId;
      if (!contextChanged) {
        return state;
      }

      const reset = createInitialMeetingState();
      reset.context = {
        roomId: event.roomId,
        meetingId: event.meetingId,
        epoch: state.context.epoch + 1,
        phase: event.meetingId ? 'joining' : 'idle'
      };
      return withPublicationIdentity(reset, event.meetingId);
    }

    case 'SESSION_ACTIVE_OBSERVED': {
      const reactivated = event.active && state.context.phase === 'ended';
      const inactivePhase: MeetingLifecyclePhase = state.context.phase === 'connected'
        ? 'ended'
        : (state.context.meetingId ? 'joining' : 'idle');
      return {
        ...state,
        context: {
          ...state.context,
          epoch: reactivated ? state.context.epoch + 1 : state.context.epoch,
          phase: event.active ? 'connected' : inactivePhase
        }
      };
    }

    case 'CAMERA_OBSERVED':
      return {
        ...state,
        camera: {
          ...state.camera,
          phase: event.streamId ? 'active' : 'off',
          generation: nextGeneration(state.camera.streamId, event.streamId, state.camera.generation),
          streamId: event.streamId,
          hasAudio: event.hasAudio,
          hasVideo: event.hasVideo
        }
      };

    case 'CAMERA_AUDIO_OBSERVED':
      return { ...state, camera: { ...state.camera, audioEnabled: event.enabled } };

    case 'CAMERA_VIDEO_OBSERVED':
      return { ...state, camera: { ...state.camera, videoEnabled: event.enabled } };

    case 'SCREEN_AVAILABILITY_OBSERVED':
      return { ...state, screen: { ...state.screen, available: event.available } };

    case 'SCREEN_OBSERVED': {
      const phase = event.active
        ? (state.screen.phase === 'stopping' ? 'stopping' : 'active')
        : (state.screen.phase === 'starting' && state.screen.desired ? 'starting' : 'off');
      return {
        ...state,
        screen: {
          ...state.screen,
          phase,
          desired: event.active ? true : (phase === 'starting' ? state.screen.desired : false),
          generation: nextGeneration(state.screen.streamId, event.streamId, state.screen.generation),
          streamId: event.streamId
        }
      };
    }

    case 'SCREEN_COMMAND_STARTED':
      return {
        ...state,
        screen: {
          ...state.screen,
          desired: event.desired,
          phase: event.desired ? 'starting' : 'stopping',
          operationId: event.operationId,
          error: null
        }
      };

    case 'SCREEN_COMMAND_SUCCEEDED':
      if (state.screen.operationId !== event.operationId) {
        return state;
      }
      return {
        ...state,
        screen: {
          ...state.screen,
          desired: event.desired,
          phase: event.desired ? 'active' : 'off',
          generation: nextGeneration(state.screen.streamId, event.streamId, state.screen.generation),
          streamId: event.desired ? event.streamId : null,
          error: null
        }
      };

    case 'SCREEN_COMMAND_FAILED':
      if (state.screen.operationId !== event.operationId) {
        return state;
      }
      return {
        ...state,
        screen: {
          ...state.screen,
          desired: false,
          phase: 'failed',
          error: errorMessage(event.error)
        }
      };

    case 'TRANSLATION_OBSERVED': {
      const providerStoppedUnexpectedly = !event.active
        && state.translation.phase === 'active'
        && state.translation.desired
        && state.context.phase === 'connected';
      const phase = event.active
        ? (state.translation.phase === 'starting' || state.translation.phase === 'stopping'
          ? state.translation.phase
          : 'active')
        : (state.translation.phase === 'starting' && state.translation.desired
          ? 'starting'
          : (providerStoppedUnexpectedly ? 'failed' : 'off'));
      return {
        ...state,
        translation: {
          ...state.translation,
          phase,
          published: event.active,
          desired: event.active ? true : (phase === 'starting' ? state.translation.desired : false),
          generation: nextGeneration(state.translation.streamId, event.streamId, state.translation.generation),
          streamId: event.streamId,
          error: providerStoppedUnexpectedly ? 'translation-provider-stopped' : state.translation.error
        }
      };
    }

    case 'TRANSLATION_COMMAND_STARTED':
      return {
        ...state,
        translation: {
          ...state.translation,
          desired: event.desired,
          phase: event.desired ? 'starting' : 'stopping',
          sourceStreamId: event.desired ? event.sourceStreamId : state.translation.sourceStreamId,
          operationId: event.operationId,
          error: null
        }
      };

    case 'TRANSLATION_COMMAND_SUCCEEDED':
      if (state.translation.operationId !== event.operationId) {
        return state;
      }
      return {
        ...state,
        translation: {
          ...state.translation,
          desired: event.desired,
          phase: event.desired ? 'active' : 'off',
          published: event.desired,
          generation: nextGeneration(state.translation.streamId, event.streamId, state.translation.generation),
          streamId: event.desired ? event.streamId : null,
          sourceStreamId: event.desired ? state.translation.sourceStreamId : null,
          error: null
        }
      };

    case 'TRANSLATION_COMMAND_FAILED':
      if (state.translation.operationId !== event.operationId) {
        return state;
      }
      return {
        ...state,
        translation: {
          ...state.translation,
          desired: false,
          phase: 'failed',
          published: false,
          streamId: null,
          error: errorMessage(event.error)
        }
      };

    default:
      return state;
  }
}
